"use client";
import React, { useState } from "react";
import { CheckCircle2, FileText } from "lucide-react";
import { Page } from "@/types/models/page";
import { generateImageSrc } from "@/utils/helper";

interface TemplatePageCardProps {
  page: Page;
  isSelected: boolean;
  onToggleSelection: (pageId: string) => void;
  viewMode?: "desktop" | "tablet" | "mobile";
}

export const TemplatePageCard: React.FC<TemplatePageCardProps> = ({
  page,
  isSelected,
  onToggleSelection,
  viewMode = "desktop",
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  
  // Safety check for page
  if (!page || !page.id) {
    return null;
  }
  
  const getAspectRatio = () => {
    switch (viewMode) {
      case "mobile":
        return "aspect-[375/667]";
      case "tablet":
        return "aspect-[768/1024]";
      default:
        return "aspect-[737/788]";
    }
  };

  const renderContent = () => {
    // Select the appropriate image based on view mode
    let imagePath: string | undefined;
    
    if (viewMode === "mobile" && page.preview_image_mobile_asset?.path) {
      imagePath = page.preview_image_mobile_asset.path;
    } else if (viewMode === "tablet" && page.preview_image_tablet_asset?.path) {
      imagePath = page.preview_image_tablet_asset.path;
    } else {
      imagePath = page.preview_image_asset?.path || 
                  page.featured_image_asset?.path || 
                  page.featured_image;
    }

    const imageClassName = viewMode === "mobile" 
      ? "w-full h-full object-cover object-top transition-transform duration-200 group-hover:scale-105 rounded-[20px]"
      : "w-full h-full object-cover object-top transition-transform duration-200 group-hover:scale-105";

    const imageContent = imagePath ? (
        <>
          {imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
              <div className="relative w-12 h-12">
                <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
                <div className="absolute inset-1 border-4 border-primary/40 rounded-full animate-spin border-t-transparent"></div>
              </div>
            </div>
          )}
          <img
            src={generateImageSrc(imagePath as string, 1024, 2000)}
            alt={page.title || page.name}
            width={1020}
            height={2000}
            className={imageClassName}
            onLoad={() => setImageLoading(false)}
            onError={(e) => {
              setImageLoading(false);
              const target = e.target as HTMLImageElement;
              target.src =
                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDYwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI2MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yNzUgMTc1SDE2NVYyMjVIMjc1VjE3NVoiIGZpbGw9IiNEOUQ5RDkiLz4KPHA+CjwvcGF0aD4KPHA+IAo8L3A+CjxwYXRoIGQ9Ik0yMTAgMTkwSDIzMFYyMTBIMjEwVjE5MFoiIGZpbGw9IiNBNkE2QTYiLz4KPC9zdmc+";
            }}
          />
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <div className="text-center text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No preview</p>
          </div>
        </div>
      );

    if (viewMode === "mobile") {
      return (
        <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/30 to-muted/50 p-4">
          <div className="relative" style={{ width: "80%", height: "90%" }}>
            <div className="absolute inset-0 overflow-hidden" style={{ padding: "30px 13px" }}>
              <div className="relative w-full h-full">
                {imageContent}
              </div>
            </div>
            <img
              src="/assets/frames/phone.png"
              alt="Phone frame"
              className="absolute inset-0 w-full h-full pointer-events-none z-10"
              style={{ objectFit: "contain" }}
            />
          </div>
        </div>
      );
    }

    if (viewMode === "tablet") {
      return (
        <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/30 to-muted/50 p-4">
          <div className="relative" style={{ width: "85%", height: "90%" }}>
            <div className="absolute inset-0 overflow-hidden" style={{ padding: "20px 11px" }}>
              <div className="relative w-full h-full">
                {imageContent}
              </div>
            </div>
            <img
              src="/assets/frames/tablet.png"
              alt="Tablet frame"
              className="absolute inset-0 w-full h-full pointer-events-none z-10"
              style={{ objectFit: "contain" }}
            />
          </div>
        </div>
      );
    }

    return imageContent;
  };

  return (
    <div
      className={`relative rounded-lg border transition-all duration-200 cursor-pointer overflow-hidden group ${
        isSelected
          ? "border-primary ring-2 ring-primary/20"
          : "border-border dark:border-auth-background hover:border-muted-foreground hover:shadow-lg"
      }`}
      onClick={() => onToggleSelection(page.id)}
    >
      {/* Checkbox */}
      <div className="absolute top-3 left-3 z-10">
        <div
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
            isSelected
              ? "bg-primary border-primary text-primary-foreground"
              : "bg-background/80 border-border backdrop-blur-sm group-hover:border-muted-foreground"
          }`}
        >
          {isSelected && <CheckCircle2 className="w-4 h-4" />}
        </div>
      </div>

      {/* Page Image */}
      <div className={`${getAspectRatio()} w-full bg-muted overflow-hidden`}>
        {renderContent()}
      </div>

      {/* Page Details */}
      <div className="p-4">
        <h4 className="font-semibold text-foreground mb-1 line-clamp-1">
          {page.title || page.name}
        </h4>
        <p className="text-sm text-muted-foreground mb-2 font-mono">
          /{page.slug}
        </p>
        {page.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {page.description}
          </p>
        )}
      </div>
    </div>
  );
};
